import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { MODULES } from './adminConstants';
import LoadingCenter from '../util/LoadingCenter';
import EmptyState from '../util/EmptyState';
import CalendarPicker from '../util/CalendarPicker';

interface Props {
  company: any;
  tariffRow: any[];
  onClose: () => void;
  onRefresh: () => void;
}

type Tab = 'general' | 'modules' | 'provisioning' | 'payments' | 'stats';

const TABS: { id: Tab; label: string }[] = [
  { id: 'general',  label: 'General' },
  { id: 'modules',  label: 'Modules' },
  { id: 'provisioning', label: 'Provisioning' },
  { id: 'payments', label: 'Payments' },
  { id: 'stats',    label: 'Stats' },
];

const PROV_META: Record<string, { label: string; icon: string }> = {
  countryDefaults: { label: 'Country defaults (locale + HR rules)', icon: '🌍' },
  publicHolidays:  { label: 'Public holidays', icon: '📅' },
  absenceTypes:    { label: 'Default absence types', icon: '🏷️' },
  defaultPattern:  { label: 'Default working pattern', icon: '🗓️' },
  demo:            { label: 'Demo data', icon: '🧪' },
};

const CompanyDetail: React.FC<Props> = ({ company, tariffRow, onClose, onRefresh }) => {
  const [tab, setTab] = useState<Tab>('general');
  const { enqueueSnackbar } = useSnackbar();

  /* ── General state ── */
  const [title, setTitle]         = useState(company.title ?? '');
  const [tariffId, setTariffId]   = useState<string>(company.tariffTypeId ?? '');
  const [paidUntil, setPaidUntil] = useState(
    company.paid_until ? moment(company.paid_until).format('YYYY-MM-DD') : '',
  );
  const [notes, setNotes]         = useState(company.notes ?? '');
  const [saving, setSaving]       = useState(false);

  /* ── Modules state ── */
  const tariff = tariffRow.find((t: any) => t.id === (tariffId ? Number(tariffId) : company.tariffTypeId)) ?? null;
  const [overrides, setOverrides] = useState<Record<string, boolean>>(company.modules_override ?? {});

  /* ── Stats state ── */
  const [stats, setStats]         = useState<Record<string, number> | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  /* ── Payments state ── */
  const [payments, setPayments]   = useState<any[]>([]);
  const [payLoading, setPayLoading] = useState(false);

  /* ── Provisioning state ── */
  const [provSteps, setProvSteps] = useState<any[] | null>(null);
  const [provLoading, setProvLoading] = useState(false);
  const [provBusy, setProvBusy] = useState<string | null>(null);

  // Reset when company changes
  useEffect(() => {
    setTitle(company.title ?? '');
    setTariffId(company.tariffTypeId ?? '');
    setPaidUntil(company.paid_until ? moment(company.paid_until).format('YYYY-MM-DD') : '');
    setNotes(company.notes ?? '');
    setOverrides(company.modules_override ?? {});
    setStats(null);
    setPayments([]);
    setProvSteps(null);
    setTab('general');
  }, [company.id]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get(`company/${company.id}/stats`);
      setStats(data);
    } catch {
      /* ignore */
    } finally {
      setStatsLoading(false);
    }
  }, [company.id]);

  const fetchPayments = useCallback(async () => {
    if (!company.stripeCustomerId) return;
    setPayLoading(true);
    try {
      const { data } = await axios.get(`billing/payments/company/${company.id}`);
      setPayments(data ?? []);
    } catch {
      /* stripe disabled — show empty */
    } finally {
      setPayLoading(false);
    }
  }, [company.id, company.stripeCustomerId]);

  const fetchProvisioning = useCallback(async () => {
    setProvLoading(true);
    try {
      const { data } = await axios.get(`company/${company.id}/provisioning`);
      setProvSteps(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    } finally {
      setProvLoading(false);
    }
  }, [company.id]);

  const runProvStep = async (step: string) => {
    setProvBusy(step);
    try {
      const { data } = await axios.post(`company/${company.id}/provisioning/${step}`);
      setProvSteps(Array.isArray(data) ? data : provSteps);
      enqueueSnackbar(t('Saved'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setProvBusy(null);
    }
  };

  useEffect(() => {
    if (tab === 'stats' && stats === null) fetchStats();
    if (tab === 'payments' && payments.length === 0) fetchPayments();
    if (tab === 'provisioning' && provSteps === null) fetchProvisioning();
  }, [tab]);

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      await axios.put(`company/superadmin/${company.id}`, {
        title,
        tariffTypeId: tariffId ? Number(tariffId) : null,
        paid_until: paidUntil || null,
        notes,
      });
      enqueueSnackbar(t('Saved'), { variant: 'success' });
      onRefresh();
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModules = async () => {
    setSaving(true);
    try {
      await axios.put(`company/superadmin/${company.id}`, { modules_override: overrides });
      enqueueSnackbar(t('Saved'), { variant: 'success' });
      onRefresh();
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetModules = async () => {
    setOverrides({});
    setSaving(true);
    try {
      await axios.put(`company/superadmin/${company.id}`, { modules_override: null });
      enqueueSnackbar(t('Saved'), { variant: 'success' });
      onRefresh();
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (mod: string) => {
    const tariffHas = tariff?.allowed_modules?.includes(mod) ?? false;
    setOverrides(prev => {
      const next = { ...prev };
      const cur = next[mod];
      if (cur === true)       { delete next[mod]; }  // override on → remove (back to tariff)
      else if (cur === false) { delete next[mod]; }  // override off → remove
      else if (tariffHas)     { next[mod] = false; } // tariff on → override off
      else                    { next[mod] = true; }  // tariff off → override on
      return next;
    });
  };

  /* ── module state for display ── */
  function getModuleState(mod: string): 'tariff' | 'override-on' | 'override-off' | 'off' {
    const ov = overrides[mod];
    const inTariff = tariff?.allowed_modules?.includes(mod) ?? false;
    if (ov === true)  return 'override-on';
    if (ov === false) return 'override-off';
    if (inTariff)     return 'tariff';
    return 'off';
  }

  function isModuleActive(mod: string) {
    const s = getModuleState(mod);
    return s === 'tariff' || s === 'override-on';
  }

  /* ── avatar ── */
  const initials = (company.title ?? '?').slice(0, 2).toUpperCase();
  let hash = 0;
  for (let i = 0; i < company.title.length; i++) hash = company.title.charCodeAt(i) + ((hash << 5) - hash);
  const avatarBg = `hsl(${hash % 360},50%,45%)`;

  return (
    <div
      className="flex flex-col bg-[var(--bg-form)]"
      style={{
        width: 460,
        minHeight: '100%',
        flexShrink: 0,
        borderLeft: '1px solid var(--border-input)',
        boxShadow: '-4px 0 20px rgba(0,0,0,.07)',
      }}>

      {/* Header */}
      <div
        className="flex items-center gap-[12px] px-[16px] py-[14px] border-b border-[var(--border-input)]"
        style={{ background: 'linear-gradient(135deg, var(--bg-top) 0%, var(--bg-form) 100%)' }}>
        <div
          className="w-[40px] h-[40px] rounded-[8px] flex items-center justify-center text-white text-[14px] font-bold flex-shrink-0"
          style={{ background: avatarBg, boxShadow: '0 2px 8px rgba(0,0,0,.2)' }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-[14px] truncate">{company.title}</div>
          <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[2px] flex items-center gap-[6px]">
            {tariff ? (
              <span className="sbadge paid">{tariff.name}</span>
            ) : (
              <span className="text-[var(--text-color-calendar-week)]">No tariff</span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn-icon"
          title="Close"
          onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Inner tabs */}
      <div className="flex border-b border-[var(--border-input)] bg-[var(--bg-top)] px-[4px]">
        {TABS.map(tb => (
          <button
            type="button"
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-[14px] py-[9px] text-[11px] font-semibold border-b-[2px] transition-all ${
              tab === tb.id
                ? 'border-[#2563eb] text-[#2563eb]'
                : 'border-transparent text-[var(--text-color-calendar-week)] hover:text-[var(--text-color)] hover:border-[var(--border-input)]'
            }`}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-[16px] py-[14px]">

        {/* ── GENERAL ── */}
        {tab === 'general' && (
          <div className="flex flex-col gap-[10px]">
            <FormRow label="Company">
              <input
                className="w-full timesheet-edit-input"
                placeholder="Company name"
                title="Company name"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </FormRow>

            <FormRow label="Tariff">
              <select
                className="w-full timesheet-edit-input"
                value={tariffId}
                onChange={e => setTariffId(e.target.value)}>
                <option value="">— no tariff —</option>
                {tariffRow.map((t: any) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </FormRow>

            <FormRow label="Paid until">
              <CalendarPicker mode="single" variant="field" output="string" value={paidUntil} onChange={(v) => setPaidUntil(v)} />
            </FormRow>

            <FormRow label="Stripe ID">
              <span className="text-[11px] font-mono text-[var(--text-color-calendar-week)]">
                {company.stripeCustomerId || '—'}
              </span>
            </FormRow>

            <FormRow label="Users">
              <span className="text-[13px]">
                {company.users_number ?? 0}
                {tariff?.max_users ? ` / ${tariff.max_users}` : ''}
              </span>
            </FormRow>

            <SectionDivider label="Notes" />
            <textarea
              className="w-full bg-[var(--bg-input)] text-[var(--text-color)] border border-[var(--border-input)] rounded-[5px] p-[8px] text-[13px] resize-y"
              rows={4}
              placeholder="Internal notes…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />

            <SectionDivider label="Activity" />
            <div className="flex flex-col gap-[6px]">
              <TimelineItem color="#1976d2" date={company.last_login} label="Last login" />
              <TimelineItem color="#2e7d32" date={company.last_payment_date} label="Last payment" />
              <TimelineItem color="#9e9e9e" date={company.createdAt} label="Company created" />
            </div>

            <div className="flex gap-[8px] mt-[6px]">
              <button type="button" className="btn-primary" onClick={handleSaveGeneral} disabled={saving}>
                {saving ? '…' : t('Save')}
              </button>
              <button type="button" className="dt-btn-ghost" onClick={onClose}>{t('Cancel')}</button>
            </div>
          </div>
        )}

        {/* ── MODULES ── */}
        {tab === 'modules' && (
          <div>
            <p className="text-[11px] text-[var(--text-color-calendar)] mb-[12px] leading-[1.5]">
              Modules are defined by the tariff. Use overrides to enable or disable individual modules for this company.
            </p>

            <div className="flex gap-[12px] text-[10px] text-[var(--text-color-calendar-week)] mb-[10px] flex-wrap">
              <LegendItem color="bg-[var(--color-info-bg,#e3f2fd)] border-[#90caf9]" label="from tariff" />
              <LegendItem color="bg-[#f1f8f2] border-[#a5d6a7]" label="override on" />
              <LegendItem color="bg-[rgba(0,0,0,.04)] border-[var(--border-input)]" label="override off / not in tariff" />
            </div>

            <div className="grid grid-cols-2 gap-[6px]">
              {MODULES.map(mod => {
                const state = getModuleState(mod);
                const active = isModuleActive(mod);
                const borderCls =
                  state === 'tariff'       ? 'border-[var(--color-info,#2563eb)] bg-[var(--color-info-bg,#e3f2fd)] opacity-80' :
                  state === 'override-on'  ? 'border-[#a5d6a7] bg-[#f1f8f2]' :
                  'border-[var(--border-input)] bg-[var(--background-color)] opacity-70';

                return (
                  <div
                    key={mod}
                    className={`flex items-center gap-[8px] px-[10px] py-[7px] rounded-[5px] border cursor-pointer transition-all ${borderCls}`}
                    onClick={() => toggleModule(mod)}>
                    <div className="flex-1">
                      <div className="text-[12px] font-medium capitalize">{mod}</div>
                      <div className="text-[9px] text-[var(--text-color-calendar-week)]">
                        {state === 'tariff' ? 'from tariff' : state === 'override-on' ? 'override on' : state === 'override-off' ? 'override off' : 'not in tariff'}
                      </div>
                    </div>
                    <Toggle on={active} />
                  </div>
                );
              })}
            </div>

            <div className="flex gap-[8px] mt-[14px]">
              <button type="button" className="btn-primary" onClick={handleSaveModules} disabled={saving}>
                {saving ? '…' : 'Save overrides'}
              </button>
              <button type="button" className="dt-btn-ghost" onClick={handleResetModules} disabled={saving}>
                Reset to tariff
              </button>
            </div>
          </div>
        )}

        {/* ── PROVISIONING ── */}
        {tab === 'provisioning' && (
          <div>
            <p className="text-[11px] text-[var(--text-color-calendar)] mb-[12px] leading-[1.5]">
              Seed steps run automatically on company creation. Re-run any step or load demo data here.
            </p>
            {provLoading && <LoadingCenter height={120} />}
            {!provLoading && provSteps && (
              <div className="flex flex-col gap-[8px]">
                {provSteps.map((s: any) => {
                  const meta = PROV_META[s.step] ?? { label: s.step, icon: '•' };
                  const isDemo = s.step === 'demo';
                  return (
                    <div key={s.step}
                      className="flex items-center gap-[10px] px-[10px] py-[8px] rounded-[5px] border"
                      style={{ borderColor: 'var(--border-input)', borderStyle: isDemo ? 'dashed' : 'solid', background: 'var(--btn-user-form)' }}>
                      <div className="w-[26px] h-[26px] rounded-[6px] flex items-center justify-center flex-shrink-0"
                        style={{ background: s.applied ? 'var(--color-success-bg)' : 'var(--bg-input)' }}>
                        {meta.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold">{meta.label}</div>
                        <div className="text-[10px] text-[var(--text-color-calendar-week)]">{s.detail || ''}</div>
                      </div>
                      <span className="text-[10px] font-semibold px-[8px] py-[2px] rounded-[10px]"
                        style={{
                          background: s.applied ? 'var(--color-success-bg)' : 'rgba(0,0,0,.06)',
                          color: s.applied ? 'var(--color-success)' : 'var(--text-color-calendar-week)',
                        }}>
                        {s.applied ? '✓ applied' : 'not applied'}
                      </span>
                      <button type="button" className="dt-btn-ghost" style={{ fontSize: 11 }}
                        disabled={provBusy === s.step || isDemo}
                        onClick={() => runProvStep(s.step)}>
                        {provBusy === s.step ? '…' : isDemo ? 'Soon' : 'Re-run'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PAYMENTS ── */}
        {tab === 'payments' && (
          <div>
            {!company.stripeCustomerId && (
              <EmptyState icon="🔗" message="No Stripe Customer ID" sub="Link a Stripe customer to see payment history" />
            )}
            {company.stripeCustomerId && payLoading && <LoadingCenter height={120} />}
            {company.stripeCustomerId && !payLoading && payments.length === 0 && (
              <EmptyState icon="💳" message="No payments yet" />
            )}
            {payments.length > 0 && (
              <table className="w-full text-[12px]" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Date', 'Amount', 'Status', 'Invoice'].map(h => (
                      <th key={h} className="text-left py-[8px] px-[10px] text-[11px] font-semibold text-[var(--text-color-calendar-week)] border-b border-[var(--border-input)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <tr key={p.id} className="border-b border-[var(--border-input)]">
                      <td className="py-[6px] px-[10px] text-[var(--text-color-calendar-week)]">{moment(p.created_at ?? p.createdAt).format('DD.MM.YYYY')}</td>
                      <td className="py-[6px] px-[10px] font-semibold text-[var(--color-success,#2e7d32)]">
                        {(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
                      </td>
                      <td className="py-[6px] px-[10px]">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="py-[6px] px-[10px] font-mono text-[11px]" style={{ color: 'var(--color-info, #2563eb)' }}>{p.stripeInvoiceId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── STATS ── */}
        {tab === 'stats' && (
          <div>
            {statsLoading && <LoadingCenter height={120} />}
            {!statsLoading && stats && (
              <div className="grid grid-cols-2 gap-[8px]">
                {STAT_ITEMS.map(({ key, label }) => (
                  <div
                    key={key}
                    className="text-center py-[12px] px-[8px] rounded-[5px] border border-[var(--border-input)] bg-[var(--background-color)]">
                    <div className="text-[20px] font-bold" style={{ color: 'var(--color-info, #2563eb)' }}>{stats[key] ?? 0}</div>
                    <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[2px]">{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Small helpers ── */

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-[10px] py-[2px]">
      <span className="text-[10px] font-bold text-[var(--text-color-calendar-week)] uppercase tracking-wider min-w-[80px] flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-[8px] mt-[8px] mb-[2px]">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-color-calendar-week)] flex-shrink-0">{label}</span>
      <div className="flex-1 h-[1px] bg-[var(--border-input)]" />
    </div>
  );
}

function TimelineItem({ color, date, label }: { color: string; date?: string | null; label: string }) {
  if (!date) return null;
  return (
    <div className="flex items-center gap-[10px] text-[12px]">
      <div className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: color }} />
      <span className="text-[var(--text-color-calendar-week)] min-w-[70px]">{moment(date).format('DD.MM.YYYY')}</span>
      <span className="text-[var(--text-color)]">{label}</span>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-[5px]">
      <span className={`inline-block w-[10px] h-[10px] rounded-[2px] border ${color}`} />
      {label}
    </div>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <div
      className={`w-[28px] h-[15px] rounded-[8px] relative flex-shrink-0 transition-colors`} style={{ background: on ? 'var(--color-info, #2563eb)' : 'rgba(0,0,0,.2)' }}>
      <div
        className="absolute w-[11px] h-[11px] rounded-full bg-white top-[2px] transition-transform"
        style={{ transform: on ? 'translateX(15px)' : 'translateX(2px)' }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    paid:      'bg-[var(--color-success-bg,#e8f5e9)] text-[var(--color-success,#2e7d32)]',
    refunded:  'bg-[var(--color-warning-bg,#fff3e0)] text-[var(--color-warning,#e65100)]',
    failed:    'bg-[var(--color-danger-bg,#ffebee)] text-[var(--color-danger,#c62828)]',
  };
  return (
    <span className={`inline-block px-[6px] py-[1px] rounded-[10px] text-[10px] font-medium ${map[status] ?? 'bg-[rgba(0,0,0,.06)] text-[var(--text-color-calendar)]'}`}>
      {status}
    </span>
  );
}

const STAT_ITEMS = [
  { key: 'users',      label: 'Active users' },
  { key: 'timesheets', label: 'Timesheet records' },
  { key: 'expenses',   label: 'Expenses' },
  { key: 'income',     label: 'Income records' },
  { key: 'equipment',  label: 'Equipment items' },
  { key: 'crmContacts',label: 'CRM contacts' },
  { key: 'documents',  label: 'Documents' },
  { key: 'training',   label: 'Training results' },
];

export default CompanyDetail;
